import { type AnnouncementItemDTO } from '@model/announcement/AnnouncementItemDTO';
import { useQuery } from '@tanstack/react-query';
import { loadAnnouncementItemsQueryKey, useLoadAnnouncementItemsQuery } from '@/hooks';
import { loadAnnouncementItemsAPI } from '@/service';

interface UseAnnouncementItemDataProps {
  announcementId: number;
  // undefined means a not-yet-saved item, i.e. a new item being created
  // rather than an existing one being edited.
  itemId: number | undefined;
}

// What we're actually editing — one of the announcement's items (of which
// this editor session targets one).
export const useAnnouncementItemData = ({
  announcementId,
  itemId,
}: UseAnnouncementItemDataProps) => {
  const { data: announcement } = useLoadAnnouncementItemsQuery(announcementId);
  const fetchedAnnouncement = announcement.data;

  // Same query cache entry as useLoadAnnouncementItemsQuery above — select
  // just reshapes the already-fetched items into an id → item map, so the
  // item being edited is looked up by its real, stable backend id instead
  // of its (unstable, copy/reorder-shifted) position in the items array.
  const { data: announcementItemsById } = useQuery({
    queryKey: loadAnnouncementItemsQueryKey(announcementId),
    queryFn: () => loadAnnouncementItemsAPI({ id: announcementId, blackAndWhite: false }),
    select: (response) =>
      new Map(
        (response.data.items ?? []).map(
          (item) => [item.id, item] as [number, AnnouncementItemDTO],
        ),
      ),
  });

  const existingItem: AnnouncementItemDTO | undefined =
    itemId != null ? announcementItemsById?.get(itemId) : undefined;

  return { fetchedAnnouncement, existingItem };
};
